import Link from "next/link";
import type { ReactNode } from "react";
import { useTranslations } from "next-intl";

import { pageHref, pagerItems } from "@/lib/site/pager";
import { cn } from "@/lib/utils";
import type { SitePagination } from "@/templates/site/types";

const itemClass =
  "inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-border px-3 text-foreground transition-colors hover:border-brand/40 hover:text-brand";

export function SitePager({
  pagination,
  baseHref,
  className,
  onPageChange,
}: {
  pagination?: SitePagination;
  baseHref: string;
  className?: string;
  onPageChange?: (page: number) => void;
}) {
  const t = useTranslations("site.ui");

  if (!pagination || !baseHref) {
    return null;
  }

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);
  if (totalPages <= 1) {
    return null;
  }

  const { page } = pagination;
  const control = (target: number, children: ReactNode, rel?: "prev" | "next") => onPageChange
    ? <button type="button" className={itemClass} onClick={() => onPageChange(target)}>{children}</button>
    : <Link href={pageHref(baseHref, target)} rel={rel} className={itemClass}>{children}</Link>;

  return (
    <nav
      aria-label={t("pagination")}
      className={cn("flex w-full flex-wrap items-center justify-center gap-1.5 text-sm", className)}
    >
      {page > 1 ? (
        control(page - 1, t("previousPage"), "prev")
      ) : null}
      {pagerItems(page, totalPages).map((item, index) =>
        item === "gap" ? (
          <span key={`gap-${index}`} className="px-1 text-muted-foreground" aria-hidden>
            …
          </span>
        ) : item === page ? (
          <span
            key={item}
            aria-current="page"
            className={cn(itemClass, "border-brand bg-brand text-brand-foreground hover:text-brand-foreground")}
          >
            {item}
          </span>
        ) : (
          <span key={item}>{control(item, item)}</span>
        ),
      )}
      {page < totalPages ? (
        control(page + 1, t("nextPage"), "next")
      ) : null}
    </nav>
  );
}
